import { useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { conciergeApi } from '../../services/concierge-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import { groupToPayload } from '../../lib/groupPayload';
import Metric from './Metric';
import HostsCard from './overview/HostsCard';
import TimeZoneCard from './overview/TimeZoneCard';
import TripDatesCard from './overview/TripDatesCard';
import type { GroupDetail as GroupDetailType } from '../../types/groups';

/**
 * The hero image, with a real file picker.
 *
 * It could only be set at creation before — by pasting a URL, which is not
 * something a host with a photo on their phone can do — so an existing group
 * could never be given one. The guest page shows this beside the story.
 */
function HeroImageCard({ group, onSaved }: { group: GroupDetailType; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pick = async (file?: File) => {
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const url = await conciergeApi.uploadGroupHeroImage(group.id, file);
      // Store it on the group only once the bytes are actually in the bucket —
      // the reverse order points the guest page at a URL that 404s.
      await groupsApi.updateGroup(group.id, groupToPayload(group, { heroImageUrl: url }));
      onSaved();
    } catch (e) {
      setError(mutationErrorMessage(e) || 'That image did not upload.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setError('');
    setBusy(true);
    try {
      await groupsApi.updateGroup(group.id, groupToPayload(group, { heroImageUrl: '' }));
      onSaved();
    } catch (e) {
      setError(mutationErrorMessage(e) || 'Could not remove the image.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Hero image</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
        Shown beside the story on the guest page. Any shape works — it is never cropped.
        JPEG, PNG or WebP.
      </Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
        {group.heroImageUrl ? (
          <Box
            component="img"
            src={group.heroImageUrl}
            alt="Current hero"
            sx={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 2, bgcolor: '#f5f5f5' }}
          />
        ) : (
          <Box sx={{ width: 120, height: 120, borderRadius: 2, bgcolor: '#f5f5f5', display: 'grid', placeItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">None yet</Typography>
          </Box>
        )}
        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? 'Uploading…' : group.heroImageUrl ? 'Replace image' : 'Upload image'}
          </Button>
          {group.heroImageUrl ? (
            <Button variant="text" disabled={busy} onClick={remove}>Remove</Button>
          ) : null}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </Stack>
    </Paper>
  );
}

/**
 * Who can open the guest page — which, until now, was decided once at creation
 * and never again.
 *
 * The two modes are not "more or less private", they are two different guest
 * experiences, and picking wrong is silent. Invite-only means the page only
 * renders for a visitor carrying an invite token: the API answers null
 * otherwise, and the guest sees "this invitation needs its original link". So a
 * broadcast that sends the shared, tokenless link — which is what a broadcast
 * sends by default, because a token is a credential and must not go out in a
 * message many people receive — is a dead end for every recipient on an
 * invite-only trip. That shipped to a live group.
 *
 * Public makes the shared link open. It does not open the roster: a visitor
 * with no token gets the public itinerary scope, room blocks without host
 * notes, and dinner availability with no claimant data. What they lose is
 * identity — no "your room", and no dinner claim — which is the reason to
 * prefer per-guest links over flipping this.
 */
function AccessModeCard({ group, onSaved }: { group: GroupDetailType; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inviteOnly = (group.accessMode || 'invite_only') === 'invite_only';

  const set = async (mode: 'public' | 'invite_only') => {
    if (mode === (group.accessMode || 'invite_only')) return;
    setError('');
    setBusy(true);
    try {
      await groupsApi.updateGroup(group.id, groupToPayload(group, { accessMode: mode }));
      onSaved();
    } catch (e) {
      setError(mutationErrorMessage(e) || 'Could not change who can open the page.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Who can open the guest page</Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <TextField
        select
        fullWidth
        value={inviteOnly ? 'invite_only' : 'public'}
        disabled={busy}
        onChange={(event) => set(event.target.value as 'public' | 'invite_only')}
        sx={{ mt: 2, maxWidth: 420 }}
        helperText={
          inviteOnly
            ? 'Only guests opening their own invite link. A shared link shows "this invitation '
              + 'needs its original link" — so broadcasts must use per-guest links.'
            : 'Anyone with the address can read the trip. No roster, no host notes, no dinner '
              + 'claimants — but also no "your room" unless the link carries their invite.'
        }
      >
        <MenuItem value="invite_only">Invite-only</MenuItem>
        <MenuItem value="public">Public — anyone with the link</MenuItem>
      </TextField>
    </Paper>
  );
}

/**
 * The story, editable.
 *
 * It was write-once: the field existed only on the create form, and this tab
 * rendered it as static text, so nothing in Studio could change it afterwards.
 * That is not a cosmetic gap — the story is the first thing a guest reads on
 * the published page, and a trip's plans move after it is written. A live group
 * spent a week telling 137 guests it was coordinating a beach day at a venue
 * the itinerary no longer went to, because the tile could be corrected and the
 * paragraph beside it could not.
 *
 * Saves through groupToPayload for the reason that function documents: the
 * update endpoint replaces the record, so the story has to travel with every
 * other field or saving it blanks them.
 */
function StoryCard({ group, onSaved }: { group: GroupDetailType; onSaved: () => void }) {
  const [draft, setDraft] = useState(group.story || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Compared against the group rather than a submitted flag, so a save that
  // lands leaves the button disabled and one that fails leaves it armed.
  const dirty = draft !== (group.story || '');

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      await groupsApi.updateGroup(group.id, groupToPayload(group, { story: draft }));
      onSaved();
    } catch (e) {
      setError(mutationErrorMessage(e) || 'Could not save the story.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Story</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
        The opening paragraph on the guest page, beside the hero image. Guests see
        this as soon as they open the invite.
      </Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <TextField
        fullWidth
        multiline
        minRows={4}
        value={draft}
        disabled={busy}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="No story added yet."
        sx={{ mt: 2 }}
      />
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save story'}
        </Button>
        {dirty ? (
          <Button variant="text" disabled={busy} onClick={() => setDraft(group.story || '')}>
            Cancel
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function OverviewTab({ group, onSaved }: { group: GroupDetailType; onSaved: () => void }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
      <Metric label="Requests" value={group.roomRequestCount} />
      <Metric label="Rooms reducing inventory" value={group.heldRoomCount} />
      <Metric label="Booked rooms" value={group.bookedRoomCount} />
      <HeroImageCard group={group} onSaved={onSaved} />
      <AccessModeCard group={group} onSaved={onSaved} />
      <TripDatesCard group={group} onSaved={onSaved} />
      <TimeZoneCard group={group} onSaved={onSaved} />
      <StoryCard group={group} onSaved={onSaved} />
      {/* Beside access mode, because both answer the same question: who can
          reach this trip, and in what capacity. */}
      <HostsCard group={group} />
    </Box>
  );
}
